import { useEffect, useState } from 'react';
import { AttendanceCalculationRule } from '../lib/attendanceCalculation';
import { OvertimeClient } from './overtimeApi';

export interface EmployeeAttendanceCalculation {
  employeeCode: string;
  rules: AttendanceCalculationRule[];
  roundUpClockIn: boolean;
  dayBreakMinutes: number;
  nightBreakMinutes: number;
}

const useEmployeeAttendanceCalculation = (
  client: OvertimeClient | null, employeeCode: string, refreshKey: string
) => {
  const [config, setConfig] = useState<EmployeeAttendanceCalculation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  useEffect(() => {
    let active = true;
    setConfig(null);
    setError(null);
    if (!employeeCode) { setLoading(false); return; }
    setLoading(true);
    const load = async () => {
      try {
        if (!client) throw new Error('接続を確認してください。');
        const { data, error: readError } = await client.rpc('get_employee_attendance_calculation', { p_employee_code: employeeCode });
        if (readError) throw readError;
        if (!data || !Array.isArray(data.rules) || typeof data.roundUpClockIn !== 'boolean' ||
            !Number.isFinite(data.dayBreakMinutes) || !Number.isFinite(data.nightBreakMinutes) ||
            !data.rules.every((rule: any) => typeof rule.employeeId === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rule.effectiveFrom) && ['minute', 'default'].includes(rule.mode))) {
          throw new Error('勤務時間の設定を確認できません。');
        }
        if (active) setConfig({ employeeCode, ...data });
      } catch (err) {
        if (active) setError('勤務時間の集計設定を取得できませんでした。打刻の保存とは別に、集計設定を再確認してください。');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [client, employeeCode, refreshKey, reloadCount]);
  return {
    config: config?.employeeCode === employeeCode ? config : null,
    error, loading, reload: () => setReloadCount(value => value + 1)
  };
};

export default useEmployeeAttendanceCalculation;
